import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';
import type { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { buildQueryString } from '../../utils/buildQueryString';
import { useDataGridInitialState } from '../useDataGridInitialState/useDataGridInitialState';

export const useDataGridState = (
  disableSearchParamSync: boolean,
  pageSizeOptions: readonly (number | { value: number; label: string })[],
  initialState: GridInitialStatePro | undefined,
  {
    pagination,
    initialPageSize,
    preventUrlReadWhenSyncDisabled = false,
  }: {
    pagination: boolean | undefined;
    initialPageSize: number;
    preventUrlReadWhenSyncDisabled?: boolean;
  }
) => {
  const [, setSearchParams] = useSearchParams();

  // extract initial state from URL and props
  const _computedInitialState = useDataGridInitialState(initialState, {
    pagination,
    initialPageSize,
  });

  // When preventUrlReadWhenSyncDisabled is true, override URL pagination with initialState or default to page 0
  const initialPaginationModel = preventUrlReadWhenSyncDisabled
    ? initialState?.pagination?.paginationModel ||
      (pagination
        ? {
            page: 0,
            pageSize: initialPageSize,
          }
        : undefined)
    : _computedInitialState?.pagination?.paginationModel;

  // Initialize paginationModel, sortModel, and filterModel from initialState or URL
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel | undefined>(
    initialPaginationModel as GridPaginationModel
  );
  // When preventUrlReadWhenSyncDisabled is true, use initialState sorting/filtering instead of URL
  const initialSortModel = preventUrlReadWhenSyncDisabled
    ? initialState?.sorting?.sortModel || _computedInitialState?.sorting?.sortModel
    : _computedInitialState?.sorting?.sortModel;
  const initialFilterModel = preventUrlReadWhenSyncDisabled
    ? initialState?.filter?.filterModel || _computedInitialState?.filter?.filterModel
    : _computedInitialState?.filter?.filterModel;
  const [sortModel, setSortModel] = useState<GridSortModel | undefined>(
    initialSortModel as GridSortModel
  );
  const [filterModel, setFilterModel] = useState<GridFilterModel | undefined>(
    initialFilterModel as GridFilterModel
  );

  // sync search params with state, update pageSize if pageSize not in pageSizeOptions
  useEffect(() => {
    // check if the pageSize is available in pageSizeOptions
    const isPageSizeAvailable = !!pageSizeOptions.find(
      (option) => option === paginationModel?.pageSize
    );

    if (pagination && paginationModel?.pageSize && !isPageSizeAvailable) {
      // get the first pageSizeOption value
      const firstPageSizeOptionValue =
        typeof pageSizeOptions[0] === 'number' ? pageSizeOptions[0] : pageSizeOptions[0].value;

      // if check if the initialPageSize is not in pageSizeOptions
      const isInitialPageSizeInOptions = pageSizeOptions.find((option) =>
        typeof option === 'number' ? option === initialPageSize : option.value === initialPageSize
      );

      // set pageSize to initialPageSize if it is in pageSizeOptions, otherwise set it to the first pageSizeOption value
      const _pageSize = isInitialPageSizeInOptions ? initialPageSize : firstPageSizeOptionValue;
      // update paginationModel state
      setPaginationModel((prev) => {
        return {
          page: prev?.page || 0,
          pageSize: _pageSize,
        };
      });
    }
  }, [initialPageSize, pageSizeOptions, pagination, paginationModel?.pageSize]);

  useEffect(() => {
    if (!disableSearchParamSync && ((pagination && paginationModel) || sortModel || filterModel)) {
      const queryString = buildQueryString({ paginationModel, sortModel, filterModel });
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        const queryParams = new URLSearchParams(queryString);

        // List of DataGrid-related params that should be removed if not in new query string
        const dataGridParams = [
          'page',
          'pageSize',
          'sort',
          'filter',
          'logicOperator',
          'quickFilter',
          'quickFilterValues',
          'quickFilterLogicOperator',
        ];

        // Remove old DataGrid params that are no longer present
        dataGridParams.forEach((key) => {
          if (!queryParams.has(key)) {
            newParams.delete(key);
          }
        });

        // Merge new query params into existing params
        queryParams.forEach((value, key) => {
          newParams.set(key, value);
        });

        return newParams;
      });
    }
  }, [
    pagination,
    disableSearchParamSync,
    paginationModel,
    sortModel,
    filterModel,
    setSearchParams,
  ]);

  return {
    initialState: _computedInitialState,
    handlePaginationModelChange: setPaginationModel,
    handleSortModelChange: setSortModel,
    handleFilterModelChange: setFilterModel,
    paginationModel: pagination ? paginationModel : undefined,
    sortModel,
    filterModel,
  };
};
